import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import routes from '../../../config/route'
import useLocale from '../../../hooks/useLocale'
import { getLocaleAppBaseUrl } from '../../../lib/utils/language'
import Header, { type HeaderLinkItem } from './Header'
import Footer from './Footer'
import PageLoader from './PageLoader'

type LayoutProps = {
  children: React.ReactNode
  showPageLoader?: boolean
}

const landingLayoutPaths = [
  '/',
  '/new',
  '/contact',
  '/team',
  '/blog',
  '/blog/[slug]',
  '/newsletter',
  '/legal-notices',
  '/privacy-policy',
  '/terms-of-use',
  '/404',
  '/500',
]

export default function Layout({
  children,
  showPageLoader = true,
}: LayoutProps) {
  const router = useRouter()
  const { t } = useTranslation('navbar')
  const { locale } = useLocale()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const shouldUseLandingLayout = landingLayoutPaths.includes(router.pathname)
  useEffect(() => {
    setIsMenuOpen(false)
  }, [router.asPath])

  const desktopLinks: HeaderLinkItem[] = [
    {
      href: getLocaleAppBaseUrl(locale, 'properties'),
      label: t('landing.projects'),
      skipLocaleHandling: true,
    },
    { href: routes.contact, label: t('landing.contact') },
    { href: routes.team, label: t('landing.team') },
    { href: routes.blog, label: t('landing.blog') },
  ]
  const mobileLinks: HeaderLinkItem[] = desktopLinks
  const ctaLink: HeaderLinkItem = {
    href: getLocaleAppBaseUrl(locale, 'register'),
    label: t('landing.cta'),
    skipLocaleHandling: true,
  }

  const content = shouldUseLandingLayout ? (
    <main className="relative">
      <Header
        isMenuOpen={isMenuOpen}
        onToggleMenu={() => setIsMenuOpen((prev) => !prev)}
        positionClassName="sticky top-0 h-0"
        navSpacingClassName=""
        containerClassName="mx-auto w-full px-3 pt-[max(0.75rem,env(safe-area-inset-top))] md:px-10 lg:px-16 xl:px-24 2xl:px-44"
        brandHref={routes.index}
        logoAlt="Shelters logo"
        desktopLinks={desktopLinks}
        mobileLinks={mobileLinks}
        ctaLink={ctaLink}
        ctaLabel={t('landing.cta')}
        mobileCtaLabel={t('landing.ctaShort')}
        mobileMenuCtaLabel={t('landing.mobileMenuCta')}
        menuAriaLabel={t('landing.menuAria')}
      />
      <div className="relative isolate overflow-hidden">
        <div className="relative z-10">
          {children}
          <Footer />
        </div>
      </div>
    </main>
  ) : (
    <main>{children}</main>
  )

  return (
    <>
      {showPageLoader && <PageLoader />}
      {content}
    </>
  )
}
